import EnhanceText from 'components/Dialogue/components/Message/EnhanceText';
import { useMemo } from 'react';
import { getFailCauseMsg } from 'hooks/business/conversation';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import dayjs from 'lib/dayjs';
import { get } from 'lodash-es';
import { cx } from '@emotion/css';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useMessageById } from 'hooks/business/message';

const MESSAGE_TYPE_KEY_MAP: Record<string, string> = {
  IMAGE: 'domain.message.type.image',
  VIDEO: 'domain.message.type.video',
  STICKER: 'domain.message.type.sticker',
  ITEM: 'domain.message.type.product',
  FAQ_LIVEAGENT: 'domain.message.type.faqLiveAgent',
  VARIATION_CARD: 'domain.message.type.variationCard',
  SYS_ASSIGN: 'domain.message.systemType.assign',
  SYS_WA_REJECT: 'domain.message.systemType.whatsAppRejected',
  SYS_INIT_CONVERSATION: 'scene.chatWorkspace.messageThread.systemMessage.initiatedConversation',
  SYS_FAQ_QUESTION: 'scene.chatWorkspace.messageThread.systemMessage.faqQuestion',
  SHOPEE_RETURN_REFUND_CARD: 'scene.chatWorkspace.messageThread.systemMessage.shopeeReturnRefund',
  recall: 'scene.chatWorkspace.messageThread.systemMessage.recall',
  JL_FIXED_REPLY: 'domain.message.type.aiAutoReply',
  JL_AI_GUIDE: 'domain.message.type.aiAutoReply',
  JL_FORM: 'domain.message.type.aiAutoReply',
  JL_ATTACHMENT: 'domain.message.type.file',
  JL_SYSTEM_MSG: 'domain.message.type.system',
  VOUCHER: 'domain.message.type.coupon',
  SYS_OUT: 'domain.message.type.system',
  ORDER: 'domain.message.type.order',
};

const getMessageTypeKey = (type: string): string => MESSAGE_TYPE_KEY_MAP[type] || '';

interface LastMessageProps {
  conversation: Conversation.Conversation;
  className?: HTMLSpanElement['className'];
}

export const LastMessage = (props: LastMessageProps) => {
  const { conversation, className } = props;
  const { t } = useTranslate();
  const sysLang = useSystemLanguageValue();
  const {
    channel,
    lastMsgId,
    failCause,
    lastMsgType,
    lastMsgContent,
    lastMsgTime,
    localExtra,
    controlStatus,
    supervisePower,
  } = conversation;

  const msg = useMessageById(lastMsgId);

  const tipTxt = useMemo(() => {
    const status = msg?.status;
    const fromUserName = msg?.fromUserName ?? '';
    if (status === 'RECALL') {
      return t('scene.chatWorkspace.messageThread.systemMessage.recall');
    } else if (failCause) {
      return getFailCauseMsg(sysLang, failCause);
    } else {
      if (lastMsgType === 'SYS_BLACKLIST') {
        try {
          const transKey = sysLangToTranslationLang(sysLang);
          const json: Conversation.MsgText = JSON.parse(lastMsgContent);
          // @ts-ignore
          const str = (
            json.translationContent?.[transKey] || json.translationContent.enUS
          )?.text.replace(
            // eslint-disable-next-line no-template-curly-in-string
            '${time}',
            dayjs(Number(lastMsgTime)).format('YYYY-MM-DD HH:mm:ss'),
          );
          return `[${str}]`;
        } catch (error) {
          //
        }
      }

      if (lastMsgType === 'FACEBOOK_MESSAGE') {
        const {
          message,
          sticker,
          attachments: { data: [{ fileUrl, imageData, videoData } = {}] = [] } = {},
        } = msg?.fbMsgContent || {};
        if (message) {
          return message;
        }
        if (sticker) {
          return `[${t('domain.message.type.sticker')}]`;
        }
        if (imageData?.url) {
          return `[${t('domain.message.type.image')}]`;
        }
        if (videoData?.url) {
          return `[${t('domain.message.type.video')}]`;
        }
        if (fileUrl) {
          return `[${t('domain.message.type.file')}]`;
        }
        return lastMsgContent;
      }

      if (localExtra) {
        const { lastMsgId: localLastMsgId, lastMsgContent: localLastMsgContent } = localExtra;
        if (
          lastMsgType !== 'TEXT' &&
          lastMsgType !== 'ATTACHMENT' &&
          lastMsgType !== 'IMAGE_WITH_TEXT' &&
          localLastMsgId === lastMsgId &&
          localLastMsgContent
        ) {
          return `[${
            lastMsgType === 'SYS_TRANSFER' && controlStatus === true && !supervisePower
              ? t('domain.message.systemType.assign')
              : t(getMessageTypeKey(lastMsgType))
          }]${localLastMsgContent}`;
        }
      }

      switch (lastMsgType) {
        case 'SYS_INIT_CONVERSATION': {
          return t('scene.chatWorkspace.messageThread.systemMessage.initiatedConversation', {
            who: fromUserName || '',
          });
        }
        case 'SYS_FAQ_QUESTION': {
          // 如果本地数据没有，则回退到lastMsgContent
          const text =
            get(msg, 'sysFaqQuestionContent')?.question || get(conversation, 'lastMsgContent');

          return t('scene.chatWorkspace.messageThread.systemMessage.faqQuestion', {
            text,
          });
        }
        case 'NOTIFICATION': {
          return msg?.notificationContent?.text ?? '';
        }
        case 'NONSUPPORT': {
          return isTiKTokUpperCase(msg?.channel)
            ? t('domain.message.type.system')
            : t('scene.chatWorkspace.messageThread.unsupported.viewOnPlatform', {
                platform: msg?.channel,
              });
        }
        case 'SYS_USER_ENTER_FROM_TRANSFER':
        case 'SYS_ALLOCATED_SERVICE': {
          return t('domain.message.type.system');
        }
        case 'IMAGE_WITH_TEXT': {
          return `[${t('domain.message.type.image')}]${lastMsgContent}`;
        }
        case 'TEXT':
        case 'ATTACHMENT': {
          return lastMsgContent;
        }
        case 'SYS_TRANSFER': {
          if (controlStatus === true && !supervisePower) {
            return `[${t('domain.message.systemType.assign')}]${lastMsgContent}`;
          }
          break;
        }
        case 'FIRST_MSG_ITEM_RCMD': {
          return t('domain.message.type.product');
        }
        default: {
          return `[${t({
            key: getMessageTypeKey(lastMsgType),
            defaultValue: t({
              key: `messageType.${lastMsgType}`,
              defaultValue: t('scene.chatWorkspace.messageThread.feedback.unknownMessageType'),
            }),
          })}]`;
        }
      }
    }
  }, [
    msg,
    lastMsgId,
    failCause,
    t,
    sysLang,
    lastMsgType,
    localExtra,
    lastMsgContent,
    lastMsgTime,
    controlStatus,
    supervisePower,
    conversation,
  ]);

  return (
    <span
      data-agent-page-exclude=""
      className={cx('text-ellipsis overflow-hidden whitespace-nowrap', className)}
      title={tipTxt}
    >
      <EnhanceText text={tipTxt} platform={channel} />
    </span>
  );
};
